import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";

export default function Registration() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    mobileNumber: "",
    gst: "",
    email: "",
    state: "",
  });
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [signupData, setSignupData] = useState(null);
  const [statesList, setStatesList] = useState([]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const fetchStates = async () => {
    try {
      const res = await axios.get("/admin/api/state");
      // handle a few possible response shapes
      if (Array.isArray(res.data)) {
        setStatesList(res.data);
        const def = res.data.find((x) => x.default || x.isDefault || x._id === form.state || x.id === form.state || x.value === form.state);
        if (def && !form.state) setForm((f) => ({ ...f, state: def._id || def.id || def.value }));
      } else if (Array.isArray(res.data?.data)) {
        setStatesList(res.data.data);
        const def = res.data.data.find((x) => x.default || x.isDefault || x._id === form.state || x.id === form.state || x.value === form.state);
        if (def && !form.state) setForm((f) => ({ ...f, state: def._id || def.id || def.value }));
      } else if (Array.isArray(res.data?.states)) {
        setStatesList(res.data.states);
        const def = res.data.states.find((x) => x.default || x.isDefault || x._id === form.state || x.id === form.state || x.value === form.state);
        if (def && !form.state) setForm((f) => ({ ...f, state: def._id || def.id || def.value }));
      }
    } catch (err) {
      // silent fallback - keep list empty
      console.error("Failed to fetch states", err);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        mobileNumber: form.mobileNumber,
        email: form.email,
        business: form.companyName,
        gstNumber: form.gst,
        state: form.state,
      };

      const response = await axios.post("/api/user/signup", payload);
      if (response.data.Status === 1) {
        setSignupData(response.data);
        toastSuccess(response.data.Message || "OTP sent to your email");
        setOtpStep(true);
      } else {
        toastError(response.data.Message || "Failed to send OTP");
      }
    } catch (err) {
      toastError(err.response?.data?.Message || "Failed to send OTP");
    }
    setIsSubmitting(false);
  };

  const handleOtpDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    setOtp(newDigits.join(""));
    if (value && index < 3) {
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");
    if (paste.length === 4) {
      const digitsArr = paste.split("").slice(0, 4);
      setOtpDigits(digitsArr);
      setOtp(digitsArr.join(""));
      document.getElementById(`otp-input-3`)?.focus();
      e.preventDefault();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 4) return;
    setIsSubmitting(true);
    try {
      const response = await axios.post("/api/user/verifyOtp", {
        email: form.email,
        otp: otp,
      });

      if (response.data.Status === 1) {
        toastSuccess("Registration successful!");
        // After successful registration + OTP verification, redirect to login
        navigate("/login");
      } else {
        toastError(response.data.Message || "Invalid OTP");
      }
    } catch (err) {
      toastError(err.response?.data?.Message || "OTP verification failed");
    }
    setIsSubmitting(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        borderColor:"2px solid #D6DBDD",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          background: "#F6F7F8",
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: "900px",
          width: "100%",
        }}
      >
        {/* LEFT SIDE IMAGE */}
        <div
          style={{
            flex: "1",
            background: "#e9edf8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <img
            src="/front-end/images/Login/login.jpg"
            alt="Registration"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "12px 0 0 12px",
            }}
          />
        </div>

        {/* RIGHT SIDE FORM */}
        <div
          style={{
            flex: "1",
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            borderColor:"1px solid #D6DBDD",
          }}
        >
          <h2
            style={{
              fontWeight: 700,
              fontSize: "1.8rem",
              marginBottom: "30px",
            }}
          >
            Registration
          </h2>

          {!otpStep ? (
            <form onSubmit={handleSendOtp} autoComplete="off">
              <div className="mb-3">
                <label style={{fontSize: "0.9rem" }}>
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="firstName"
                  type="text"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label style={{fontSize: "0.9rem" }}>
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="lastName"
                  type="text"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label style={{fontSize: "0.9rem" }}>
                  Company Name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="companyName"
                  type="text"
                  value={form.companyName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label style={{fontSize: "0.9rem" }}>
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      fontSize: "1rem",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/4/41/Flag_of_India.svg"
                      alt="IN"
                      width="20"
                      style={{ marginRight: "4px" }}
                    />
                    +91
                  </span>
                  <input
                    className="form-control"
                    name="mobileNumber"
                    type="text"
                    value={form.mobileNumber}
                    onChange={handleChange}
                    required
                    maxLength={10}
                    pattern="[0-9]*"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label style={{fontSize: "0.9rem" }}>
                  GST Number <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="gst"
                  type="text"
                  value={form.gst}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-4">
                <label style={{fontSize: "0.9rem" }}>
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
               <div className="mb-4">
                <label style={{fontSize: "0.9rem" }}>
                 State <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select state</option>
                  {statesList.map((s) => (
                    <option key={s._id || s.id || s.value} value={s._id || s.id || s.value}>
                      {s.name || s.title || s.label || s.stateName}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="btn w-100"
                disabled={isSubmitting}
                style={{
                  backgroundColor: "#638225", 
                  color: "#fff",    
                  border: "none",
                  fontWeight: "500",
                }}
              >
                {isSubmitting ? "Sending..." : "Send OTP"}
              </button>
              <p style={{ marginTop: "12px" }}>
                    Already have an account? <Link to="/login"><u>Login here</u></Link>
                  </p>
            </form>
          ) : (
            <div>
              <div className="mb-4">
                  <label style={{fontSize: "0.9rem" }}>Email <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  type="email"
                  value={form.email}
                  disabled
                />
              </div>

              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <p style={{marginBottom: "15px" }}>
                  Enter the 4-digit OTP sent to your email
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "20px",
                  }}
                >
                  {otpDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`otp-input-${idx}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpDigitChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      onPaste={handleOtpPaste}
                      className="form-control"
                      style={{
                        width: "45px",
                        textAlign: "center",
                        fontSize: "22px",
                      }}
                      disabled={isSubmitting}
                      autoFocus={idx === 0}
                    />
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={handleVerifyOtp}
                className="btn bg-blue w-100"
                disabled={isSubmitting || otp.length < 4}
                style={{
                  backgroundColor: "#638225",
                  color: "#fff",
                  border: "none",
                  fontWeight: "500",
                  fontSize: "1rem",
                }}
              >
                {isSubmitting ? "Verifying..." : "Verify OTP"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setOtpStep(false);
                  setOtpDigits(["", "", "", ""]);
                  setOtp("");
                }}
                className="btn btn-link w-100 mt-2"
              >
                Back to Registration
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
